import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  useActiveSupportSession,
  useEndSupportSession,
} from "@/hooks/api/superadmin";
import { useAuthStore } from "@/stores/auth";

/**
 * Sticky banner shown when a superadmin (KraetdTech ops) is actively viewing
 * tenant data. Always-visible reminder + audit-trail signal.
 *
 * Architecture: outer component does the role gate, inner does the polling.
 * Splitting like this means non-superadmin users never mount the API hook,
 * so we never hit the endpoint and never accumulate hook-order issues across
 * HMR reloads.
 */
export function SupportSessionBanner() {
  const isSuperadmin = useAuthStore(
    (s) => s.user?.is_superadmin === true
  );
  if (!isSuperadmin) return null;
  return <SupportSessionBannerInner />;
}

function SupportSessionBannerInner() {
  const { data: session } = useActiveSupportSession();
  const endSession = useEndSupportSession();

  if (!session) return null;

  const minutesAgo = Math.floor(
    (Date.now() - new Date(session.started_at).getTime()) / 60_000
  );

  return (
    <div
      role="alert"
      className="sticky top-0 z-50 flex items-center justify-between gap-3 border-b border-danger/40 bg-danger px-4 py-2 text-sm text-white shadow-modal"
    >
      <div className="flex min-w-0 items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="font-semibold">SUPPORT SESSION ACTIVE</span>
        <span className="hidden truncate sm:inline">
          — viewing {session.tenant_name}. Reason: &ldquo;{session.reason}&rdquo;. Started{" "}
          {minutesAgo} min ago.
        </span>
      </div>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => endSession.mutate(session.id)}
        loading={endSession.isPending}
        className="bg-white text-danger hover:bg-slate-100"
      >
        End session
      </Button>
    </div>
  );
}
